import {
  JsonData,
  JsonObject,
  jsonDataIsArrayWithIds,
  jsonDataIsObj,
  jsonDataIsObjWithId,
} from './types'

// Converts a given set of data into a string format, in which all
// values have been sorted in a consistent and repeatable way.
export function sortStringify(data: JsonData): string {
  return JSON.stringify(_sortArraysAndProps(data))
}

function sortKey(data: JsonData) {
  ;(jsonDataIsObjWithId(data) ? `${data.id}|` : '-----|') + JSON.stringify(data)
}

export function _sortArraysAndProps(data: JsonData): JsonData {
  if (Array.isArray(data)) {
    const output = data.map(_sortArraysAndProps)
    output.sort((x, y) => {
      const xStr = sortKey(x)
      const yStr = sortKey(y)
      if (xStr < yStr) {
        return -1
      }
      if (yStr < xStr) {
        return 1
      }
      return 0
    })
    return output
  }
  if (jsonDataIsObj(data)) {
    const output: JsonObject = {}
    const props = Object.keys(data)
    props.sort()
    for (const prop of props) {
      output[prop] = _sortArraysAndProps(data[prop])
    }
    return output
  }
  return data
}

export function summarize(data: JsonData) {
  if (Array.isArray(data)) {
    if (data.length === 0) {
      return '[]'
    }
    const dtype = typeof data[0]
    for (const item of data) {
      if (typeof item !== dtype) {
        return `<mixed>[${data.length}]`
      }
    }
    return `<${dtype}>[${data.length}]`
  } else if (jsonDataIsObj(data)) {
    return `{ ${Object.keys(data)
      .map((x) => (x === 'id' ? `id: "${data.id}"` : x))
      .join(', ')}}`
  } else {
    return JSON.stringify(data)
  }
}

export function showDiff(data1: JsonData, data2: JsonData, indent?: string): string[] {
  const sort1 = _sortArraysAndProps(data1)
  const sort2 = _sortArraysAndProps(data2)

  if (jsonDataIsArrayWithIds(sort1) && jsonDataIsArrayWithIds(sort2)) {
    let i1 = 0
    let i2 = 0
    const output: string[] = []
    while (i1 < sort1.length && i2 < sort2.length) {
      if (sort1[i1].id === sort2[i2].id) {
        output.push(...showDiff(sort1[i1], sort2[i2], `${indent ?? ''}  `))
        i1++
        i2++
      } else if (sort1[i1].id < sort2[i2].id && i1 < sort1.length) {
        output.push(`--- ${indent ?? ''}${summarize(sort1[i1])}`)
        i1++
      } else {
        output.push(`+++ ${indent ?? ''}${summarize(sort2[i2])}`)
        i2++
      }
    }
    if (output.length) {
      return [`${indent ?? ''}[`, ...output, `${indent ?? ''}],`]
    }
    return []
  } else if (
    !Array.isArray(sort1) &&
    !Array.isArray(sort2) &&
    jsonDataIsObj(sort1) &&
    jsonDataIsObj(sort2)
  ) {
    const output: string[] = []
    for (const prop in sort1) {
      if (prop in sort2) {
        const diff = showDiff(sort1[prop], sort2[prop], `${indent ?? ''}  `)
        if (diff.length > 0) {
          output.push(`    ${indent ?? ''}${prop}:`)
          output.push(...diff)
        }
      } else {
        output.push(`--- ${indent ?? ''}${prop}: ${summarize(sort1[prop])},`)
      }
    }
    for (const prop in sort2) {
      if (!(prop in sort1)) {
        output.push(`+++ ${indent ?? ''}${prop}: ${summarize(sort1[prop])},`)
      }
    }
    if (output.length) {
      return [`${indent ?? ''}{`, ...output, `${indent ?? ''}},`]
    }
    return []
  }
  if (JSON.stringify(sort1) === JSON.stringify(sort2)) {
    return []
  }
  return [`--- ${indent ?? ''}${summarize(sort1)}`, `+++ ${indent ?? ''}${summarize(sort2)},`]
}
